import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import classes from "./OrderForm.module.css";
import shared from "./OrderShared.module.css";
import { useSelector, useDispatch } from "react-redux";
import { updateQuantity, removeItem } from "../redux/slices/cartSlice";
import { selectProducts } from "../redux/slices/productsSlice";
import { categories } from "../Pages/Orders/Orders";
import { generateOptions } from "../Pages/Orders/CategoryPageTemplate";
import { saveOrderToLocalStorage } from "../utils/orderStorage";
import { formatIsraeliNow } from "../utils/dateTime";

const normalizeIsraeliPhone = (value = "") => {
  const digits = value.replace(/\D/g, "");

  if (digits.startsWith("972") && digits.length >= 11) {
    return `0${digits.slice(3)}`;
  }

  if (digits.startsWith("0")) {
    return digits;
  }

  return value.trim();
};

const isValidIsraeliPhone = (value = "") => {
  const normalized = normalizeIsraeliPhone(value);
  return /^0(5\d{8}|[2-489]\d{7})$/.test(normalized);
};

export default function OrderForm() {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const cartItems = useSelector((state) => state.cart.items);
  const allProducts = useSelector(selectProducts);

  const phoneRef = useRef(null);

  useEffect(() => {
    const customerProfile = JSON.parse(localStorage.getItem("customerProfile"));
    if (customerProfile) {
      setFormData({
        ...customerProfile,
        desiredDateInput: customerProfile.desiredDateInput,
      });
    }
  }, []);

  // Enrich cart items with product data from the global products list
  const enrichedCartItems = cartItems.map((item) => {
    const product = allProducts.find((p) => p.id === item.productId);
    return {
      ...item,
      ...product,
      userCommentAnswer: item.userCommentAnswer || "",
    };
  });

  // עדכן שדה בטופס
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // שלח הטופס
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (enrichedCartItems.length === 0) {
      setError("העגלה ריקה. אנא הוסף פריטים לפני השליחה.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setError("");

    const rawPhone = phoneRef.current?.value?.trim() || formData.phone || "";

    if (!isValidIsraeliPhone(rawPhone)) {
      setError("מספר הטלפון אינו תקין");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const israeliPhone = normalizeIsraeliPhone(rawPhone);

    setLoading(true);

    try {
      // כאן תוכל להוסיף קריאה API לשרת כדי לשמור את ההזמנה
      // עבור עכשיו, נשמור בLocalStorage ונעבור לעמוד תודה

      const userDetails = {
        ...formData,
        phone: israeliPhone,
        orderedAt: formatIsraeliNow(),
      };

      // שמור נתונים ב-localStorage (עבור עכשיו)
      localStorage.setItem("customerProfile", JSON.stringify(userDetails));

      // נעבור לעמוד סקירה שבו הלקוח יכול לשלוח את בקשת ההזמנה
      setTimeout(() => {
        navigate("/orders/review");
      }, 500);
    } catch (err) {
      setError("הייתה בעיה בשליחת ההזמנה. אנא נסה שוב.");
      setLoading(false);
      console.error("Order submission error:", err);
    }
  };

  return (
    <bdo dir="rtl">
      <div className={`${classes.formContainer} ${shared.orderPageContainer}`}>
        <div className={classes.formHeader}>
          <h1 className={shared.pageTitle}>טופס הזמנה</h1>
          <p className={shared.pageSubtitle}>
            מלא את הטופס והמתן לקשר ישר ממנו עם הצעת מחיר
          </p>
        </div>

        {error && <div className={classes.alert}>{error}</div>}

        <form onSubmit={handleSubmit} className={classes.form}>
          <div className={classes.formGrid}>
            <div className={classes.formField}>
              <label className={shared.fieldLabel}>שם מלא *</label>
              <input
                type="text"
                className={shared.inputField}
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="לדוגמה: דוד כהן"
              />
            </div>

            <div className={classes.formField}>
              <label className={shared.fieldLabel}>שם חברה/עסק</label>
              <input
                type="text"
                className={shared.inputField}
                name="company"
                value={formData.company}
                onChange={handleChange}
                placeholder="לדוגמה: חנות הפרחים של דוד"
              />
            </div>

            <div className={classes.formField}>
              <label className={shared.fieldLabel}>כתובת אימייל *</label>
              <input
                type="email"
                className={shared.inputField}
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="example@email.com"
              />
            </div>

            {/* <div className={classes.formField}>
              <label className={shared.fieldLabel}>מספר טלפון *</label>
              <div className={classes.userPhone}>
                <input
                  type="tel"
                  className={shared.inputField}
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  placeholder="05X-XXX-XXXX"
                />
                <button
                  type="button"
                  // onClick={verifyPhone}
                  className={classes.verifyButton}
                >
                  לחץ לאימות
                </button>
              </div>
            </div>
          </div> */}

            <div className={classes.formField}>
              <label className={shared.fieldLabel}>תאריך איסוף רצוי *</label>
              <input
                type="date"
                className={shared.inputField}
                name="desiredDateInput"
                value={formData.desiredDateInput || ""}
                onChange={(e) => {
                  const customerPickupDate = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    desiredDateInput: customerPickupDate,
                    desiredDate: new Date(
                      `${customerPickupDate}T12:00:00`,
                    ).toLocaleDateString("he-IL", {
                      weekday: "long",
                      year: "numeric",
                      month: "2-digit",
                      day: "2-digit",
                      timeZone: "Asia/Jerusalem",
                    }),
                  }));
                }}
                required
                min={new Date().toISOString().split("T")[0]}
              />
            </div>
            <div className={classes.formField}>
              <label className={shared.fieldLabel}>מספר טלפון *</label>
              <input
                ref={phoneRef}
                type="tel"
                id="phone"
                name="phone"
                className={shared.inputField}
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          {/* בחירה קטגוריה */}
          {/* הצגה של עגלת קניות עם פריטים שנוספו - ניתן להזמין מספר קטגוריות בהזמנה אחת */}
          <div className={classes.cartBox}>
            <h2>עגלת הזמנה</h2>
            {enrichedCartItems.length === 0 && (
              <p className={classes.cartEmpty}>
                העגלה ריקה. הוסף מוצרים מדפי הקטגוריות.
              </p>
            )}
            {enrichedCartItems.map((item) => (
              <div key={item.productId} className={classes.cartItem}>
                <div className={classes.cartItemInfo}>
                  <p className={classes.cartItemName}>
                    {item.name} (
                    {categories.find((cat) => cat.id === item.category)?.name ||
                      item.category}
                    )
                  </p>
                  <div className={classes.cartItemMeta}>
                    {/* <span>כמות: {item.quantity}</span> */}
                    <span>
                      כמות:{" "}
                      <select
                        className={`${shared.inputField} ${classes.quantitySelect}`}
                        value={item.quantity}
                        onChange={(e) => {
                          const itemToUpdate = {
                            productId: item.productId,
                            quantity: Number(e.target.value),
                          };
                          dispatch(updateQuantity(itemToUpdate));

                          saveOrderToLocalStorage(
                            "UpdateQuantity",
                            cartItems,
                            itemToUpdate,
                          );
                        }}
                      >
                        {generateOptions(item)}
                      </select>
                    </span>
                  </div>
                  {item.comment && (
                    <div className={classes.cartItemNote}>
                      <strong>הערה למוצר:</strong> {item.comment}
                    </div>
                  )}
                  {item.userCommentAnswer && (
                    <div className={classes.cartItemResponse}>
                      {item.needsCommentAnswer && (
                        <strong>תשובתך: {item.userCommentAnswer}</strong>
                      )}
                      {!item.needsCommentAnswer && (
                        <strong>{item.userCommentAnswer}</strong>
                      )}
                    </div>
                  )}
                  {/* {item.userCommentAnswer ? (
                    <div className={classes.cartItemResponse}>
                      <strong>תשובתך:</strong> {item.userCommentAnswer}
                    </div>
                  ) : item.comment ? (
                    <div className={classes.cartItemResponse}>
                      <strong>תשובתך:</strong> טרם נשלחה
                    </div>
                  ) : null} */}
                </div>
                <div className={classes.cartItemActions}>
                  {/* <input
                    type="number"
                    min={0}
                    value={item.quantity}
                    onChange={(e) =>
                      dispatch(
                        updateQuantity({
                          productId: item.productId,
                          quantity: Number(e.target.value),
                        }),
                      )
                    }
                  /> */}

                  <button
                    type="button"
                    className={classes.removeButton}
                    onClick={() => {
                      dispatch(removeItem(item.productId));
                      saveOrderToLocalStorage("RemoveItem", cartItems, item);
                    }}
                  >
                    הסר
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* כמות משוערת */}
          {/* כמות משוערת - אופציונלי, ההזמנה הסופית תתבסס על פריטי העגלה */}
          {/* <div className={classes.formField}>
            <label>כמות משוערת</label>
            <input
              type="number"
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
              placeholder="לדוגמה: 50 יחידות"
              min={1}
            />
          </div> */}

          <div className={classes.formField}>
            <label className={shared.fieldLabel}>הערות ודרישות נוספות</label>
            <textarea
              className={shared.textareaField}
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={5}
              placeholder="תיאור מדויק של מה שאתה מחפש, דרישות מיוחדות, וכן הלאה..."
            />
          </div>

          {/* הודעה חשובה */}
          <div className={classes.importantBox}>
            <p className={classes.importantText}>
              ⚠️ <strong>אנא שים לב:</strong> יתכן שאנחנו נצור איתך קשר בתוך 24
              שעות. אנא הקדש לנו כתובת אימייל וטלפון תקינים. לא הוזכר מחיר בשלב
              זה - זה יבוא לאחר בדיקת זמינות.
            </p>
          </div>

          {/* כפתורים */}
          <div className={shared.actionButtons}>
            <button
              type="submit"
              className={`${shared.buttonBase} ${shared.buttonPrimary}`}
              disabled={loading}
            >
              {loading ? "מעביר..." : "המשך לבדיקת פרטים"}
            </button>
            <button
              type="button"
              className={`${shared.buttonBase} ${shared.buttonSecondary}`}
              onClick={() => navigate("/orders")}
              disabled={loading}
            >
              חזור לקטגוריות
            </button>
          </div>
        </form>
      </div>
    </bdo>
  );
}
